import { useMemo } from 'react';
import ProfileSetupStep from '@components/onboarding/profile-setup-step.component';
import CalendarsSetupStep from '@components/onboarding/calendars-setup-step.component';
import UserSettingsStep from '@components/onboarding/user-settings-step.component';
import AddUsersStep from '@components/onboarding/add-users-step.component';
import PromptsStep from '@components/onboarding/prompts-step.component';
import ToggleAutoScheduleStep from '@components/onboarding/toggle-auto-schedule.component';
import {
  handleProfileSetupSubmit,
  handleSchedulingSubmit,
  handleUserSettingsSubmit,
  handlePromptsSubmit,
  handleAddUsersSubmit
} from '@util/helpers/onboarding.helpers';
import { useUserStore } from '@store/app.store';
import { useIsUserType, useStyledToast } from '@util/hooks';
import {
  usePatchPerson,
  useCreatePersonWithInvite
} from '@services/query-services/people.query-service';
import { usePatchTenantAsAppUser } from '@services/query-services/tenants.query-service';

/**
 * Custom hook for managing onboarding steps configuration and submission handlers
 * @param {Object} params
 * @param {Object} params.form - React Hook Form instance
 * @param {Function} params.setIsNextButtonDisabled - state Function to control next button state
 * @returns {OnboardingStep[]} Array of step configurations
 */
export function useOnboardingSteps({ form, setIsNextButtonDisabled }) {
  const user = useUserStore((state) => state.user);
  const setUser = useUserStore((state) => state.setUser);
  const refreshSessionTenant = useUserStore((state) => state.refreshSessionTenant);
  const isAdmin = useIsUserType('Admin');
  const tenant = useUserStore((state) => state.tenant);
  const toast = useStyledToast();

  const { mutateAsync: createPersonWithInvite } = useCreatePersonWithInvite();
  const { mutateAsync: patchPerson } = usePatchPerson();
  const { mutateAsync: patchTenantAsAppUser } = usePatchTenantAsAppUser();

  const steps = useMemo(
    () => [
      {
        label: 'Profile Setup',
        title: 'Upload your profile photo',
        content: <ProfileSetupStep form={form} />,
        onStepSubmit: async () => {
          const formValues = form.getValues();
          await handleProfileSetupSubmit({
            formValues,
            user,
            tenant,
            patchPerson,
            patchTenantAsAppUser,
            setUser,
            refreshSessionTenant,
            toast
          });
        }
      },
      {
        label: 'Scheduling',
        title: 'Auto-scheduling',
        content: <ToggleAutoScheduleStep form={form} />,
        onStepSubmit: async () => {
          const formValues = form.getValues();
          await handleSchedulingSubmit({
            formValues,
            user,
            patchPerson,
            setUser,
            toast
          });
        }
      },
      {
        label: 'Calendar Setup',
        title: 'Connect your calendar',
        content: <CalendarsSetupStep setIsNextButtonDisabled={setIsNextButtonDisabled} />
      },
      {
        label: 'User Settings',
        title: 'User Settings',
        content: <UserSettingsStep form={form} />,
        onStepSubmit: async () => {
          const formValues = form.getValues();
          await handleUserSettingsSubmit({
            formValues,
            user,
            patchPerson,
            setUser,
            toast
          });
        }
      },
      {
        label: 'AI Context',
        title: 'Customize your Summaries',
        content: <PromptsStep form={form} />,
        onStepSubmit: async () => {
          const formValues = form.getValues();
          await handlePromptsSubmit({
            formValues,
            user,
            tenant,
            patchPerson,
            patchTenantAsAppUser,
            setUser,
            refreshSessionTenant,
            toast
          });
        }
      },
      ...(isAdmin
        ? [
            {
              label: 'Add Users',
              title: 'Add Users to Organization',
              content: <AddUsersStep form={form} />,
              onStepSubmit: async () => {
                const formValues = form.getValues();
                await handleAddUsersSubmit({
                  formValues,
                  createPersonWithInvite,
                  toast
                });
              }
            }
          ]
        : [])
    ],
    [form, user, tenant, setIsNextButtonDisabled]
  );

  return steps;
}
